import React from 'react'
import {View} from 'react-native'
import Markdown from 'react-native-markdown-display'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useFocusEffect} from '@react-navigation/native'

import {useMarkdownLinkPress} from '#/lib/hooks/useMarkdownLinkPress'
import {usePalette} from '#/lib/hooks/usePalette'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {s} from '#/lib/styles'
import {useSetMinimalShellMode} from '#/state/shell'
import {ViewHeader} from '#/view/com/util/ViewHeader'
import {CenteredView} from '#/view/com/util/Views'
import {useTheme} from '#/alf'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {findArticle} from '#/content/help'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'HelpArticle'>
export const HelpArticleScreen = ({route}: Props) => {
  const pal = usePalette('default')
  const {_} = useLingui()
  const t = useTheme()
  const setMinimalShellMode = useSetMinimalShellMode()
  const handleLinkPress = useMarkdownLinkPress()
  const article = findArticle(route.params.category, route.params.slug)

  useFocusEffect(
    React.useCallback(() => {
      setMinimalShellMode(false)
    }, [setMinimalShellMode]),
  )

  const markdownStyles = {
    body: {
      color: pal.text.color,
      fontSize: 16,
      lineHeight: 24,
    },
    heading1: {
      fontSize: 28,
      fontWeight: 'bold' as const,
      color: pal.text.color,
      marginTop: 20,
      marginBottom: 10,
    },
    heading2: {
      fontSize: 24,
      fontWeight: 'bold' as const,
      color: pal.text.color,
      marginTop: 20,
      marginBottom: 10,
    },
    heading3: {
      fontSize: 20,
      fontWeight: 'bold' as const,
      color: pal.text.color,
      marginTop: 20,
      marginBottom: 10,
    },
    paragraph: {
      marginBottom: 10,
      color: pal.text.color,
    },
    listItem: {
      marginBottom: 5,
      color: pal.text.color,
    },
    listItemBullet: {
      color: pal.text.color,
    },
    strong: {
      fontWeight: 'bold' as const,
    },
    em: {
      fontStyle: 'italic' as const,
    },
    link: {
      color: pal.link.color,
    },
    hr: {
      backgroundColor: pal.border.borderColor,
      height: 1,
      marginVertical: 20,
    },
  }

  if (!article) {
    return (
      <Layout.Screen>
        <ViewHeader title={_(msg`Help`)} />
        <CenteredView>
          <View style={[s.p20]}>
            <Text style={[{fontSize: 16}, t.atoms.text_contrast_medium]}>
              <Trans>Article not found.</Trans>
            </Text>
          </View>
        </CenteredView>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen>
      <ViewHeader title={article.title} />
      <CenteredView>
        <View style={[s.p20]}>
          <Markdown style={markdownStyles} onLinkPress={handleLinkPress}>
            {article.content}
          </Markdown>
        </View>
      </CenteredView>
    </Layout.Screen>
  )
}
